import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api, API } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Play, Pause, SkipForward, X, Check, FileDown, Timer, Save,
  ChevronLeft, ChevronRight, Loader2, AlertCircle,
} from 'lucide-react';

// Mode "Cours en salle / RDV pédagogique" — plein écran, navigation au clavier,
// chronomètre, notes en direct, validation par étape, génération PDF compte-rendu.

function StepRenderer({ step }) {
  if (step.type === 'article') {
    const url = `https://codevoiture.fr/magazine/${step.ref}`;
    return (
      <div className="text-white/90">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Article magazine</div>
        <p className="text-white/75 text-[18px] leading-relaxed mb-4">Lecture commentée de l'article. Projeter et discuter point par point.</p>
        <Link to={`/magazine/${step.ref}`} target="_blank" rel="noopener" className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white/[0.06] border border-white/15 text-white text-[12.5px] hover:bg-white/[0.1]">
          Ouvrir l'article · /magazine/{step.ref}
        </Link>
      </div>
    );
  }
  if (step.type === 'simulator') {
    return (
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Simulateur interactif</div>
        <p className="text-white/75 text-[18px] leading-relaxed mb-4">Faire manipuler les sliders devant la classe — les élèves observent l'impact en direct.</p>
        <Link to={step.ref || '/simulateurs'} target="_blank" rel="noopener" className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] font-semibold">
          Ouvrir le simulateur
        </Link>
      </div>
    );
  }
  if (step.type === 'data') {
    return (
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Données officielles</div>
        <p className="text-white/75 text-[18px] leading-relaxed mb-4">Présenter le baromètre ONISR le plus récent — chiffres clés du mois.</p>
        <Link to="/onisr" target="_blank" rel="noopener" className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white/[0.06] border border-white/15 text-white text-[12.5px]">
          Ouvrir le baromètre ONISR
        </Link>
      </div>
    );
  }
  if (step.type === 'discussion') {
    return (
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Échange dirigé</div>
        {step.questions?.length ? (
          <ul className="space-y-3 text-white/90 text-[20px] leading-relaxed">
            {step.questions.map((q, i) => (<li key={i} className="flex gap-3"><span className="text-red-500 font-serif-editorial text-[24px] shrink-0">{i+1}.</span>{q}</li>))}
          </ul>
        ) : (
          <p className="text-white/75 text-[19px] leading-relaxed">Discussion ouverte avec l'élève et / ou ses représentants légaux sur le thème de cette étape.</p>
        )}
      </div>
    );
  }
  if (step.type === 'progress') {
    return (
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Bilan de progression</div>
        <p className="text-white/75 text-[18px] leading-relaxed mb-3">Présenter la progression REMC de l'élève — projeter le livret depuis l'espace enseignant.</p>
        <p className="text-white/50 text-[14px]">Astuce : ouvre /enseignant/dashboard dans un autre onglet pour avoir les chiffres sous les yeux.</p>
      </div>
    );
  }
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Synthèse</div>
      <p className="text-white/75 text-[19px] leading-relaxed">Reformuler les points clés avec l'élève · Engagement pour la prochaine séance.</p>
    </div>
  );
}

export default function SalleSeancePage() {
  const { id: seanceId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [seance, setSeance] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [notes, setNotes] = useState('');
  const [summary, setSummary] = useState('');
  const [completed, setCompleted] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const tickRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== 'enseignant') { navigate('/'); return; }
    api.getSeance(seanceId).then((s) => {
      setSeance(s);
      setNotes(s.notes_live || '');
      setSummary(s.notes_summary || '');
      setCompleted(s.completed_steps || []);
      if (s.status === 'in_progress') setRunning(true);
    }).catch(() => setSeance({ error: 'Séance introuvable' }));
  }, [loading, user, seanceId, navigate]);

  // Timer
  useEffect(() => {
    if (!running) { if (tickRef.current) clearInterval(tickRef.current); return; }
    tickRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(tickRef.current);
  }, [running]);

  // Keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (e.target?.tagName === 'TEXTAREA' || e.target?.tagName === 'INPUT') return;
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); nextStep(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); setStepIdx((i) => Math.max(0, i - 1)); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seance, stepIdx]);

  if (loading || !seance) return <div className="min-h-screen bg-black text-white flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (seance.error) return <div className="min-h-screen bg-black text-white flex items-center justify-center gap-3"><AlertCircle /> {seance.error}</div>;

  const steps = seance.steps_snapshot || [];
  const step = steps[stepIdx];

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const start = async () => {
    setRunning(true);
    if (seance.status !== 'in_progress' && seance.status !== 'done') {
      try { await api.updateSeance(seanceId, { action: 'start' }); } catch (_) {}
    }
  };

  const nextStep = () => {
    if (!completed.includes(stepIdx)) setCompleted((c) => [...c, stepIdx].sort((a, b) => a - b));
    setStepIdx((i) => Math.min(steps.length - 1, i + 1));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.updateSeance(seanceId, { notes_live: notes, notes_summary: summary, completed_steps: completed });
      setSavedAt(new Date().toLocaleTimeString('fr-FR'));
    } finally { setSaving(false); }
  };

  const complete = async () => {
    if (!window.confirm('Terminer la séance et générer le compte-rendu ?')) return;
    setSaving(true);
    try {
      await api.updateSeance(seanceId, { notes_live: notes, notes_summary: summary, completed_steps: completed, action: 'complete' });
      // Download PDF using fetch + blob (Authorization header required)
      const r = await fetch(api.seancePdfUrl(seanceId), { headers: { Authorization: `Bearer ${localStorage.getItem('cv_token')}` } });
      if (r.ok) {
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `seance_${seanceId.slice(0, 8)}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }
      navigate('/enseignant/seances');
    } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    // Use authenticated fetch + blob download
    const r = await fetch(api.seancePdfUrl(seanceId), { headers: { Authorization: `Bearer ${localStorage.getItem('cv_token')}` } });
    if (!r.ok) { alert('Erreur PDF'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `seance_${seanceId.slice(0, 8)}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const stepDuration = step?.duration ? step.duration * 60 : 0;

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-white/10 bg-[#0a0a0b]/95 backdrop-blur px-4 lg:px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button data-testid="salle-close" onClick={() => navigate('/enseignant/seances')} className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/10 flex items-center justify-center hover:bg-white/[0.1]" aria-label="Quitter">
              <X size={15} />
            </button>
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold truncate">{seance.category} · {seance.audience.replace('_', ' & ')}</div>
              <h1 className="font-serif-editorial text-white text-[18px] lg:text-[22px] leading-tight truncate">{seance.title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div data-testid="salle-timer" className="flex items-center gap-2 px-3 h-9 rounded-full bg-white/[0.06] border border-white/15 text-white font-mono-spec text-[13px]">
              <Timer size={14} className={running ? 'text-red-400' : 'text-white/50'} /> {mmss(elapsed)}
            </div>
            {!running ? (
              <button data-testid="salle-start" onClick={start} className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] font-semibold"><Play size={13} /> Démarrer</button>
            ) : (
              <button data-testid="salle-pause" onClick={() => setRunning(false)} className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-white/[0.06] border border-white/15 text-white text-[12.5px]"><Pause size={13} /> Pause</button>
            )}
            <button data-testid="salle-save" onClick={save} disabled={saving} className="hidden md:inline-flex items-center gap-2 h-9 px-4 rounded-full bg-white/[0.06] border border-white/15 text-white text-[12.5px] hover:bg-white/[0.1] disabled:opacity-50">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} {savedAt ? `Sauvé ${savedAt}` : 'Sauver'}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-5 sm:py-8 grid lg:grid-cols-[1fr_320px] gap-4 sm:gap-6">
        {/* Slide principal */}
        <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-gradient-to-br from-[#0d0d0e] to-[#080809] p-5 sm:p-8 lg:p-12 min-h-[50vh] sm:min-h-[60vh] flex flex-col">
          <div className="flex items-center justify-between mb-5 sm:mb-7">
            <div className="text-[11px] sm:text-[12px] uppercase tracking-[0.2em] text-white/40 font-mono-spec">Étape {stepIdx + 1} / {steps.length}</div>
            <div className="text-[11px] sm:text-[12px] text-white/40 font-mono-spec">Élève : <span className="text-white/80">{seance.student_name}</span></div>
          </div>

          <h2 data-testid="salle-step-title" className="font-serif-editorial text-white text-[24px] sm:text-[40px] lg:text-[56px] leading-[1.08] sm:leading-[1.05] tracking-tight mb-3">{step?.title}</h2>
          {stepDuration > 0 && <div className="text-white/45 text-[12px] sm:text-[13px] font-mono-spec mb-5 sm:mb-7">⏱ {step.duration} min prévues</div>}

          <div className="mt-2 flex-1">
            {step && <StepRenderer step={step} />}
          </div>

          {/* Navigation footer */}
          <div className="mt-7 sm:mt-10 flex items-center justify-between gap-2 sm:gap-3 pt-4 sm:pt-6 border-t border-white/5 flex-wrap">
            <button data-testid="salle-prev" onClick={() => setStepIdx((i) => Math.max(0, i - 1))} disabled={stepIdx === 0} className="inline-flex items-center gap-2 h-10 sm:h-11 px-4 sm:px-5 rounded-full bg-white/[0.04] border border-white/10 text-white text-[12.5px] sm:text-[13.5px] hover:bg-white/[0.08] disabled:opacity-40">
              <ChevronLeft size={15} /> Précédent
            </button>
            <div className="text-[11px] text-white/40 font-mono-spec hidden lg:block">← → ou Espace pour naviguer</div>
            {stepIdx < steps.length - 1 ? (
              <button data-testid="salle-next" onClick={nextStep} className="inline-flex items-center gap-2 h-10 sm:h-11 px-4 sm:px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] sm:text-[13.5px] font-semibold">
                Étape suivante <SkipForward size={15} />
              </button>
            ) : (
              <button data-testid="salle-complete" onClick={complete} disabled={saving} className="inline-flex items-center gap-2 h-10 sm:h-11 px-4 sm:px-5 rounded-full bg-green-600 hover:bg-green-500 text-white text-[12.5px] sm:text-[13.5px] font-semibold disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={15} />} Terminer · PDF
              </button>
            )}
          </div>
        </div>

        {/* Sidebar — étapes + notes */}
        <aside className="space-y-5">
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 font-bold mb-3">Déroulé</div>
            <ol className="space-y-2">
              {steps.map((st, i) => {
                const done = completed.includes(i);
                const active = i === stepIdx;
                return (
                  <li key={i}>
                    <button data-testid={`salle-step-${i}`} onClick={() => setStepIdx(i)} className={`w-full flex items-center gap-2 p-2.5 rounded-lg text-left text-[12.5px] transition border ${active ? 'border-red-500 bg-red-950/30 text-white' : done ? 'border-green-700/30 bg-green-950/15 text-white/85' : 'border-white/8 bg-white/[0.02] text-white/65 hover:bg-white/[0.05]'}`}>
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${done ? 'bg-green-600 text-white' : 'bg-white/10 text-white/70'}`}>{done ? <Check size={12} /> : i + 1}</span>
                      <span className="flex-1 truncate">{st.title}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 font-bold mb-3">Notes en direct</div>
            <textarea
              data-testid="salle-notes-live"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Réactions de l'élève, points à approfondir…"
              className="w-full h-32 p-3 rounded-lg bg-white/[0.04] border border-white/10 text-white text-[13px] outline-none focus:border-red-500 resize-none"
            />
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 font-bold mb-3">Synthèse & axes de travail</div>
            <textarea
              data-testid="salle-notes-summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Synthèse projetée en fin de séance + axes pour la prochaine fois"
              className="w-full h-28 p-3 rounded-lg bg-white/[0.04] border border-white/10 text-white text-[13px] outline-none focus:border-red-500 resize-none"
            />
          </div>

          {seance.status === 'done' && (
            <button data-testid="salle-download-pdf" onClick={downloadPdf} className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-white/[0.06] border border-white/15 text-white text-[13.5px] font-semibold hover:bg-white/[0.1]">
              <FileDown size={14} /> Re-télécharger le PDF
            </button>
          )}
        </aside>
      </div>
    </div>
  );
}
